// FR-O2 join flow: `/invite?token=...` from the email `orgs::create_invite` sends. Deliberately
// public (GET /org-invites/{token} needs no Actor) so the preview — "join Acme as member" —
// renders before asking the visitor to sign in or sign up; accepting itself still requires a
// verified-email session (orgs::accept_invite).
import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, ApiFail } from "../api/client";
import type { InvitePreviewOut, OrgOut } from "../api/types";
import { useSession } from "../session";
import { AuthShell } from "../components/AuthShell";
import { FormNotice, LoadingButton } from "../components/form";

export default function InviteAcceptPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const navigate = useNavigate();
  const { user, loading: sessionLoading, refresh } = useSession();

  const [preview, setPreview] = useState<InvitePreviewOut | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [accepting, setAccepting] = useState(false);
  const [acceptError, setAcceptError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    api<InvitePreviewOut>("GET", `/org-invites/${encodeURIComponent(token)}`)
      .then(setPreview)
      .catch((err) => {
        setLoadError(
          err instanceof ApiFail && err.code === "not_found"
            ? "This invite link is no longer valid — it may have been revoked or already used."
            : "Couldn't load this invite.",
        );
      });
  }, [token]);

  async function onAccept() {
    if (!token) return;
    setAccepting(true);
    setAcceptError(null);
    try {
      const org = await api<OrgOut>("POST", "/org-invites/accept", { token });
      await refresh();
      navigate(`/o/${org.slug}`, { replace: true });
    } catch (err) {
      if (err instanceof ApiFail) {
        setAcceptError(
          err.code === "not_found"
            ? "This invite isn't for your signed-in account."
            : err.message,
        );
      } else {
        setAcceptError("Couldn't reach Grackle. Check your connection and try again.");
      }
    } finally {
      setAccepting(false);
    }
  }

  if (!token) {
    return (
      <AuthShell title="Invite link" subtitle="This link is missing its invite token.">
        <FormNotice tone="error">Check the link you were sent and try again.</FormNotice>
      </AuthShell>
    );
  }

  if (loadError) {
    return (
      <AuthShell title="Invite link" subtitle="This invite couldn't be loaded.">
        <FormNotice tone="error">{loadError}</FormNotice>
      </AuthShell>
    );
  }

  if (!preview || sessionLoading) {
    return (
      <AuthShell title="Invite link" subtitle="Loading…">
        <div />
      </AuthShell>
    );
  }

  if (preview.expired) {
    return (
      <AuthShell title="This invite has expired" subtitle={`Ask an admin at ${preview.org_name} to send a new one.`}>
        <FormNotice tone="error">Invites expire a week after they're sent.</FormNotice>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title={`Join ${preview.org_name}`}
      subtitle={`You've been invited as a${preview.role === "admin" ? "n" : ""} ${preview.role} — for ${preview.email}.`}
      footer={
        !user ? (
          <>
            Wrong account? <Link to="/signin">Sign in</Link> or <Link to="/signup">create one</Link> with{" "}
            {preview.email}.
          </>
        ) : undefined
      }
    >
      {!user ? (
        <div className="auth-form">
          <FormNotice>Sign in or create an account with {preview.email}, then come back to this link.</FormNotice>
          <LoadingButton block onClick={() => navigate("/signin")}>
            Sign in to accept
          </LoadingButton>
        </div>
      ) : (
        <div className="auth-form">
          {user.email.toLowerCase() !== preview.email.toLowerCase() ? (
            <FormNotice tone="error">
              You're signed in as {user.email}, but this invite is for {preview.email}. Sign in as that
              address to accept it.
            </FormNotice>
          ) : null}
          {acceptError ? <FormNotice tone="error">{acceptError}</FormNotice> : null}
          <LoadingButton
            block
            loading={accepting}
            disabled={user.email.toLowerCase() !== preview.email.toLowerCase()}
            onClick={onAccept}
          >
            {accepting ? "Joining…" : `Join ${preview.org_name}`}
          </LoadingButton>
        </div>
      )}
    </AuthShell>
  );
}
